import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

export type SchoolRequestRequestDto = {
  schoolName: string;
  email: string;
  mobileNo: string;
  addressLine1: string;
  addressLine2?: string;
  district: string;
  state: string;
  pincode: string;
  status: string;
  description: string;
  captcha?: string;
};

export type SchoolRequest = {
  id?: number;
  schoolName?: string;
  email?: string;
  mobileNo?: string;
  addressLine1?: string;
  addressLine2?: string;
  district?: string;
  state?: string;
  pincode?: string;
  status?: string;
  description?: string;
  createdAt?: any;
  updatedAt?: any;
  requestId?: string;
};

const SCHOOL_REQUEST_API_URL = `${process.env.REACT_APP_API_URL}/school-request`;

export type PageResponse<T> = {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
  numberOfElements: number;
  empty: boolean;
};

export const getAllSchoolRequests = async (params: {
  page: number;
  size: number;
}): Promise<PageResponse<SchoolRequest>> => {
  const response = await axios.get(SCHOOL_REQUEST_API_URL, {
    params: { page: params.page, size: params.size },
  });
  return response.data;
};

const createSchoolRequest = async (
  schoolRequestRequestDto: SchoolRequestRequestDto
): Promise<SchoolRequest> => {
  const response = await axios.post(SCHOOL_REQUEST_API_URL, schoolRequestRequestDto, {
    withCredentials: true,
  });
  return response.data;
};

const updateSchoolRequest = async (args: {
  id: number;
  data: SchoolRequestRequestDto;
}): Promise<SchoolRequest> => {
  const response = await axios.put(`${SCHOOL_REQUEST_API_URL}/${args.id}`, args.data);
  return response.data;
};

export function useSchoolRequest() {
  const queryClient = useQueryClient();
  const createSchoolRequestMutation = useMutation({
    mutationFn: createSchoolRequest,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["school-requests"] });
    },
  });

  const updateSchoolRequestMutation = useMutation({
    mutationFn: updateSchoolRequest,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["school-requests"] });
    },
  });

  const useSchoolRequests = (params: { page: number; size: number }) =>
    useQuery({
      queryKey: ["school-requests", params.page, params.size],
      queryFn: () => getAllSchoolRequests(params),
      keepPreviousData: true,
    });

  return {
    createSchoolRequestMutation,
    updateSchoolRequestMutation,
    useSchoolRequests,
  };
}
